import React from "react";

import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";

import { MopButton } from "client/components";
import { useStyles } from "client/domain/OnboardingStatus/Steps/Integrations/styles";
import type {
  IntegrationStage,
  PlatformIntegration,
} from "client/domain/OnboardingStatus/Steps/Integrations/types";
import { useBranding, useMerchantModel, useRunOnce } from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { getAfterpayShopifyLink } from "client/pages/OnboardingStatus/utils";
import { getEmail } from "client/utils/getEmail";

export const Shopify: React.FC<PlatformIntegration> = ({
  handleClickVerifyGatewayEnabled,
}) => {
  const { brandKey, brandTitle } = useBranding();
  const [
    integrationStage,
    setIntegrationStage,
  ] = React.useState<IntegrationStage>("SignIn");
  const { merchant } = useMerchantModel();
  const classes = useStyles();

  const merchantEmail = getEmail(merchant);

  const registeredCountry =
    merchant?.companyInformation?.registeredCountry?.value;

  const afterpayPaymentPage = getAfterpayShopifyLink(
    brandKey,
    registeredCountry
  );

  useRunOnce(() =>
    logAmplitudeEvent("STATUS_PAGE", "integration_card_shopify_loaded")
  );

  return (
    <Box display="flex">
      <Box ml={2}>
        <ul className={classes.list}>
          <li>
            <Typography className={classes.shopifyHeader} variant="body2">
              Go to Shopify Payments Page
            </Typography>
            <MopButton
              className={clsx(classes.shortButton, {
                [classes.disabledButton]: integrationStage !== "SignIn",
              })}
              mopVariant={integrationStage === "SignIn" ? "primary" : "default"}
              onClick={() => {
                setIntegrationStage("Verify");
                logAmplitudeEvent(
                  "STATUS_PAGE",
                  "integration_card_shopify_link_clicked"
                );
                window.open(afterpayPaymentPage, "_blank");
              }}
            >
              Sign in to Shopify
            </MopButton>
          </li>
          <li>
            <Typography className={classes.shopifyHeader} variant="body2">
              Click on the <strong>Connect</strong> button under{" "}
              <strong>Account status</strong>.
            </Typography>
          </li>
          <li>
            <Typography className={classes.shopifyHeader} variant="body2">
              Select your store&apos;s country and enter your email{" "}
              <strong>{merchantEmail}</strong>. You will get a code from your
              email you will need to link your account.
            </Typography>
          </li>
          <li>
            <Typography className={classes.shopifyHeader} variant="body2">
              Be sure to go back to your Shopify payment page. Then, click on
              the green ‘<strong>Activate {brandTitle} New</strong>’ button.
            </Typography>
            <MopButton
              className={classes.shortButton}
              mopVariant={integrationStage === "Verify" ? "primary" : "default"}
              onClick={() => {
                handleClickVerifyGatewayEnabled();
                logAmplitudeEvent(
                  "STATUS_PAGE",
                  "integration_card_shopify_complete"
                );
                setIntegrationStage("Complete");
              }}
              disabled={integrationStage === "SignIn"}
            >
              Confirm and continue
            </MopButton>
          </li>
        </ul>
      </Box>
    </Box>
  );
};
